<template>
  <div
    ref="viewport"
    class="share-viewport"
    :class="{'is-interactive': interactive}"
    v-on="viewportListeners"
  >
    <div
      class="share-inner"
      :style="transformStyle"
      @dblclick="toggleZoom"
    >
      <MediaTrackRenderer
        class="share-video"
        v-bind="$attrs"
      />
    </div>

    <iRow
      v-if="zoom > 1"
      width="hug"
      position="absolute"
      :position-placements="{top: 'unset', bottom: '0.6rem', left: 'unset', right: '0.6rem'}"
      :z-index="10"
      @click.stop
    >
      <iButton
        variant="secondary"
        size="small"
        data-test="share-reset-view"
        @click="reset"
      >
        Reset view
      </iButton>
    </iRow>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia";
import MediaTrackRenderer from "%/apps/video-engine/components/video-call/shared/MediaTrackRenderer.vue";
import { useVideoCallUiStore } from "%/apps/video-engine/stores/video-call-ui-store";

export default {
  name: "ScreenShareViewport",
  components: { MediaTrackRenderer },
  inheritAttrs: false,
  props: {
    peerId: {
      type: String,
      required: true,
    },
    interactive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      dragging: false,
      dragStart: null,
      startOffset: null,
    };
  },
  computed: {
    ...mapState(useVideoCallUiStore, ["screenShareViewports"]),
    viewportListeners() {
      if (!this.interactive) {
        return {};
      }

      return { wheel: this.onWheel, mousedown: this.onPointerDown, touchstart: this.onPointerDown };
    },
    zoom() {
      if (!this.interactive) {
        return 1;
      }
      const state = this.screenShareViewports?.[this.peerId];
      return state?.zoom || 1;
    },
    offset() {
      if (!this.interactive) {
        return { x: 0, y: 0 };
      }
      const state = this.screenShareViewports?.[this.peerId];
      return state?.offset || { x: 0, y: 0 };
    },
    transformStyle() {
      const { x, y } = this.offset;
      return {
        transform: `translate(${x}px, ${y}px) scale(${this.zoom})`,
      };
    },
  },
  beforeUnmount() {
    this.onPointerUp();
  },
  methods: {
    ...mapActions(useVideoCallUiStore, [
      "setScreenShareZoom",
      "setScreenShareOffset",
      "resetScreenShareViewport",
    ]),
    onWheel(event) {
      event.preventDefault();
      const delta = event.deltaY;
      const factor = delta > 0 ? 0.9 : 1.1;
      this.setScreenShareZoom(this.peerId, this.zoom * factor);
      this.clampAndSyncOffset(this.offset);
    },
    onPointerDown(event) {
      const point = this.getPoint(event);
      this.dragging = true;
      this.dragStart = point;
      this.startOffset = { ...this.offset };
      window.addEventListener("mousemove", this.onPointerMove);
      window.addEventListener("mouseup", this.onPointerUp);
      window.addEventListener("touchmove", this.onPointerMove, { passive: false });
      window.addEventListener("touchend", this.onPointerUp);
    },
    onPointerMove(event) {
      if (!this.dragging || this.zoom <= 1) {
        return;
      }
      event.preventDefault();
      const point = this.getPoint(event);
      const dx = point.x - this.dragStart.x;
      const dy = point.y - this.dragStart.y;
      this.clampAndSyncOffset({
        x: this.startOffset.x + dx,
        y: this.startOffset.y + dy,
      });
    },
    onPointerUp() {
      this.dragging = false;
      window.removeEventListener("mousemove", this.onPointerMove);
      window.removeEventListener("mouseup", this.onPointerUp);
      window.removeEventListener("touchmove", this.onPointerMove);
      window.removeEventListener("touchend", this.onPointerUp);
    },
    getPoint(event) {
      if (event.touches && event.touches[0]) {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }
      return { x: event.clientX, y: event.clientY };
    },
    toggleZoom(event) {
      if (!this.interactive) {
        return;
      }
      event.stopPropagation();
      this.reset();
    },
    reset() {
      if (this.zoom > 1) {
        this.resetScreenShareViewport(this.peerId);
        return;
      }
      this.setScreenShareZoom(this.peerId, 2);
    },
    clampAndSyncOffset(offset) {
      const clamped = this.clampOffset(offset);
      this.setScreenShareOffset(this.peerId, clamped);
    },
    clampOffset(offset) {
      if (!this.$refs.viewport) {
        return offset;
      }
      const rect = this.$refs.viewport.getBoundingClientRect();
      const extraWidth = rect.width * (this.zoom - 1);
      const extraHeight = rect.height * (this.zoom - 1);
      const maxX = extraWidth > 0 ? extraWidth / 2 : 0;
      const maxY = extraHeight > 0 ? extraHeight / 2 : 0;
      return {
        x: Math.max(Math.min(offset.x, maxX), -maxX),
        y: Math.max(Math.min(offset.y, maxY), -maxY),
      };
    },
  },
};
</script>

<!--
  XXX: Structural only. This is a pan/zoom viewport over a <video>: the
  transform, the transform-origin and the grab cursor are the mechanics of
  dragging a surface, which iCL does not model. No colours, sizes or spacing
  live here any more.
-->
<style scoped>
.share-viewport {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.share-viewport.is-interactive {
  cursor: grab;
}

.share-viewport.is-interactive:active {
  cursor: grabbing;
}

.share-inner {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  transition: transform 0.1s ease-out;
}

.share-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

</style>
